import type { CrossFamilyLink } from './familyEvidenceService';
import { apiFetch, apiGetOrNull, apiPost, apiPut, apiDelete, csrfFetch, getApiUrl, signOutOnExpiredSession } from './api';
import { lookupUsers, formatUserName, type UserSummary } from './userService';
import type { Contract, ContractPdf, Clause, ClauseComment, ContractComment, BoundingBox } from '@/types';
import type { SavedPdfHighlight } from '@/types/contract-analysis';
import type { FoldedChild } from '@/lib/contractFold';
import type { RelatedContractDto } from './foldedContracts';
import { asTier } from '@/lib/risk';
import { zipRiskBreakdown } from '@/lib/riskFindings';

interface PersistedCommentInput {
  comment: string
  createdAt: string
  userId?: string
  /** Optional client-generated idempotency key — the server MERGEs the comment on it (ADR 0010). */
  clientId?: string
}

interface PersistedHighlightInput {
  highlightText: string
  createdAt: string
  userId?: string
  coordinates?: CoordinatePoint[]
  /** Optional reviewer note saved as a Comment on the highlight. */
  comment?: string | null
  /** Optional client-generated idempotency key — the server MERGEs the highlight on it (ADR 0010). */
  clientId?: string
}

function toCommentInputVariable(comment: PersistedCommentInput): {
  comment: string
  createdAt: string
  userId: string | null
  clientId: string | null
} {
  return {
    comment: comment.comment,
    createdAt: comment.createdAt,
    userId: comment.userId ?? null,
    clientId: comment.clientId ?? null,
  }
}

export async function getContract(contractUuid: string): Promise<Contract> {
  const contract = await apiFetch<Contract>(`contracts/${contractUuid}`);
  // Document-level comments arrive carrying only userId; resolve authors to display names
  // (mirrors hydrateClauseComments) so the thread never shows a raw UUID.
  contract.comments = await hydrateContractComments(contract.comments ?? []);
  // Tiers (the contract's own and the ones inside its risk findings) go through `asTier` so the
  // permanent legacy-colour backstop applies on this path too: a dump-restored database still
  // serves red/amber/green (ADR 0026).
  contract.riskTier = asTier(contract.riskTier);
  contract.riskBaselineTier = asTier(contract.riskBaselineTier);
  contract.riskFindings = (contract.riskFindings ?? []).map((f) => ({
    ...f,
    weight: asTier(f.weight),
    minTier: asTier(f.minTier),
  }));
  return contract;
}

export async function getContractPdf(contractUuid: string): Promise<ContractPdf> {
  return apiFetch<ContractPdf>(`contracts/${contractUuid}/pdf`);
}

/** One entry of the caller's "Shared with me" vault section (`ContractController.SharedContractDTO`). */
export interface SharedContract {
  uuid: string;
  nameOfAgreement: string | null;
  fileName: string | null;
  projectUuid: string;
  level: 'READER' | 'EDITOR' | 'ADMIN';
  /** How the grant reaches this contract: a direct contract grant, or a granted folder. */
  via: 'DIRECT' | 'FOLDER';
}

/**
 * Contracts the caller can open without any membership: their direct contract grants plus the
 * contracts of folders granted to them (ADR 0025). Self-scoped — feeds the vault's "Shared with
 * me" section, and the analysis page's redaction of related-contract links in contract-only mode.
 */
export async function getSharedWithMe(): Promise<SharedContract[]> {
  return apiFetch<SharedContract[]>('contracts/shared-with-me');
}

/** The contract-level fields editable from the analysis top bar's settings (gear) dialog. */
export interface ContractAgreementDetails {
  nameOfAgreement: string;
  /** ISO yyyy-MM-dd, or null to clear a wrongly extracted date. */
  dateOfAgreement: string | null;
}

/**
 * Update contract-level agreement details. Both fields are applied as given: the name is
 * required (the server rejects a blank one with 400); a null date clears the stored one.
 * Requires EDITOR+ access.
 */
export async function updateContractAgreementDetails(
  contractUuid: string,
  details: ContractAgreementDetails
): Promise<void> {
  await apiFetch<void>(`contracts/${contractUuid}/agreement-details`, {
    method: 'PATCH',
    body: JSON.stringify(details),
  });
}

/**
 * Permanently deletes a contract and everything it owns (clauses, justifications,
 * comments, highlights, etc.) on the server. Requires project-admin (tenancy)
 * access — the server returns 403 otherwise. Irreversible.
 *
 * Uses a raw fetch rather than apiFetch because the endpoint returns no body:
 * 204 when the graph cleanup was confirmed in-request, 202 when the record is
 * deleted but the graph cleanup is still queued (it converges in the background).
 */
export async function deleteContract(
  contractUuid: string
): Promise<{ pendingGraphCleanup: boolean }> {
  const response = await csrfFetch(getApiUrl(`contracts/${contractUuid}`), { method: 'DELETE' });
  if (!response.ok) {
    if (response.status === 401) {
      signOutOnExpiredSession();
    }
    if (response.status === 403) {
      throw new Error('You don’t have permission to delete this contract — only a project admin can remove it.');
    }
    throw new Error(`Failed to delete contract: ${response.status} ${response.statusText}`);
  }
  return { pendingGraphCleanup: response.status === 202 };
}

function transformComment(c: any, userMap: Map<string, UserSummary>): ClauseComment {
  const user = c.userId ? userMap.get(c.userId) : undefined
  return {
    uuid: c.uuid,
    comment: c.comment,
    createdAt: c.createdAt,
    userId: c.userId,
    userName: formatUserName(user, c.userId),
    userEmail: user?.email ?? undefined,
  }
}

// Collect all userIds referenced by comments on the given clauses and
// fetch the corresponding user summaries in a single batch request.
async function enrichClauseCommentUsers(clauses: any[]): Promise<Map<string, UserSummary>> {
  const userIds: string[] = []
  for (const clause of clauses) {
    for (const comment of clause?.comments ?? []) {
      if (comment?.userId) userIds.push(comment.userId)
    }
  }
  if (userIds.length === 0) return new Map()
  return lookupUsers(userIds)
}

/**
 * Resolves the authors of document-level comments to display names, batching the user lookup.
 * The `{uuid, comment, createdAt, userId}` shape comes from both the contract read (getContract)
 * and the management-account read (getManagementAccount); the resolved `userName` is what the
 * thread renders, so a raw UUID is never shown (falls back to a short "User xxxxxxxx" if the id
 * has no matching user).
 */
export async function hydrateContractComments(
  comments: { uuid: string; comment: string; createdAt: string; userId?: string | null }[],
): Promise<ContractComment[]> {
  const userIds = comments
    .map((c) => c.userId)
    .filter((id): id is string => Boolean(id))
  const userMap = userIds.length ? await lookupUsers(userIds) : new Map<string, UserSummary>()
  return comments.map((c) => {
    const user = c.userId ? userMap.get(c.userId) : undefined
    return {
      uuid: c.uuid,
      comment: c.comment,
      createdAt: c.createdAt,
      userId: c.userId ?? undefined,
      // Resolve to a real name, else a short "User xxxxxxxx" label from the id — never the raw UUID.
      // Left undefined when there's no userId so the thread's neutral "Reviewer" placeholder shows.
      userName: c.userId ? formatUserName(user, c.userId) : undefined,
      userEmail: user?.email ?? undefined,
    }
  })
}

/**
 * Resolves comment authors and sorts each clause's comments chronologically.
 * Shared by contract and management-account clause fetches.
 */
export async function hydrateClauseComments(clauses: any[]): Promise<Clause[]> {
  const userMap = await enrichClauseCommentUsers(clauses)
  return clauses.map(clause => ({
    ...clause,
    comments: clause.comments
      ?.map((c: any) => transformComment(c, userMap))
      .sort((a: ClauseComment, b: ClauseComment) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) || []
  }));
}

/**
 * Adapts one clause field-bag from the semantic layer's viewer projection (the /data/read clause
 * shape) into the frontend {@link Clause}. The one real transform is `typeAssignments`
 * (each `{uuid, certainty, justification, type: {clauseType}, details}`) → `clauseJustifications`
 * (`{uuid, clauseType: {clauseType}, certainty, justification, additionalDetails}`); everything else —
 * tags, bounding boxes, comments (left raw for {@link hydrateClauseComments}), validity issues, and the
 * incoming amendedBy/supersededBy edges — already matches. Shared with managementAccountService (FE-2).
 */
export function adaptSemanticClause(raw: any): Clause {
  return {
    uuid: raw.uuid,
    text: raw.text ?? null,
    heading: raw.heading ?? undefined,
    clauseTypeTags: (raw.clauseTypeTags ?? []).map((t: any) => ({ uuid: t.uuid, clauseType: t.clauseType, code: t.code })),
    // Tags the reviewer flagged as incorrect (persisted on FLAGGED_INCORRECT_TYPE). Carried so the
    // flagged-as-wrong overlay/panel re-renders persisted flags on reload, not just same-session ones.
    incorrectClauseTypeTags: (raw.incorrectClauseTypeTags ?? []).map((t: any) => ({ uuid: t.uuid, clauseType: t.clauseType, code: t.code })),
    clauseJustifications: (raw.typeAssignments ?? []).map((ta: any) => ({
      uuid: ta.uuid,
      clauseType: { clauseType: ta.type?.clauseType, code: ta.type?.code },
      certainty: ta.certainty,
      justification: ta.justification,
      additionalDetails: (ta.details ?? []).map((d: any) => ({ name: d.name, value: d.value, values: d.values })),
    })),
    boundingBoxes: raw.boundingBoxes ?? undefined,
    comments: raw.comments ?? [], // raw {uuid, comment, createdAt, userId}; hydrateClauseComments resolves authors
    validityIssues: raw.validityIssues ?? undefined,
    // Incoming amendment edges — the "Updated" badge (see clauseUpdateRef). Already in the
    // {uuid, heading, contract:{uuid, nameOfAgreement}} ref shape.
    amendedBy: raw.amendedBy ?? undefined,
    supersededBy: raw.supersededBy ?? undefined,
    // Backend-persisted Clause Risk with provenance (ADR 0023) — the viewer projection carries
    // these; RiskBadge renders them verbatim. Tiers go through `asTier` so the permanent
    // legacy-colour backstop applies here too: a dump-restored database still serves
    // red/amber/green on this path (ADR 0026).
    riskTier: asTier(raw.riskTier),
    riskBaselineTier: asTier(raw.riskBaselineTier),
    // The clause's score + Risk Score Breakdown (zipped from the served parallel arrays).
    // Displayed only to admins — the modal gates rendering, not this adapter.
    riskScore: raw.riskScore ?? null,
    riskBreakdown: zipRiskBreakdown(raw.riskBreakdownTexts, raw.riskBreakdownPoints, raw.riskBreakdownKinds),
    riskReasons: raw.riskReasons ?? null,
    riskDecidedBy: raw.riskDecidedBy ?? null,
    riskJustification: raw.riskJustification ?? null,
    riskEvaluatedAt: raw.riskEvaluatedAt ?? null,
    // The floor firings that raised this clause's tier — what a clause-scoped risk breakdown shows.
    // Tiers inside them go through `asTier` for the same legacy-colour reason as above.
    riskFindings: (raw.riskFindings ?? []).map((f: any) => ({
      uuid: f.uuid ?? null,
      key: f.key,
      kind: f.kind,
      label: f.label ?? null,
      category: f.category ?? null,
      weight: asTier(f.weight),
      points: f.points ?? 0,
      minTier: asTier(f.minTier),
      clauseUuid: raw.uuid,
    })),
  };
}

export async function getContractClauses(projectUuid: string, contractUuid: string): Promise<Clause[]> {
  // READ-1: GET the contract's full clause graph from the semantic layer (no folding). Returns the
  // contract field-bag (with a `clauses` list), or null when the contract isn't in the project.
  const contract = await apiGetOrNull<{ clauses?: any[] }>(
    `projects/${projectUuid}/data/contracts/${contractUuid}/clauses`,
  );
  return hydrateClauseComments((contract?.clauses ?? []).map(adaptSemanticClause));
}

// ─── Extraction-run history (read-only "view a previous version") ───────────────
// A contract's extraction runs and a single PRIOR run's clauses, for the viewer's
// version selector. The CURRENT run is read through getContractClauses above; only
// older runs use the run-scoped endpoint, which reaches superseded clauses directly
// (bypassing the live isFinal filter) and returns the same clause shape.

export interface ExtractionRunSummary {
  runUuid: string
  runNumber: number | null
  status: string | null
  createdAt: string | null
  finishedAt: string | null
  promptVersionId: string | null
  taxonomyVersionId: string | null
  model: string | null
  category: string | null
  /** The live run — its clauses are what every other surface shows. Read it via getContractClauses. */
  current: boolean
}

/** A contract's extraction runs, newest first. Empty for legacy contracts with no recorded runs. */
export async function getContractRuns(
  projectUuid: string,
  contractUuid: string,
): Promise<ExtractionRunSummary[]> {
  return (
    (await apiGetOrNull<ExtractionRunSummary[]>(
      `projects/${projectUuid}/data/contracts/${contractUuid}/runs`,
    )) ?? []
  )
}

/** Read one PRIOR run's clauses (read-only history). Same shape as getContractClauses. */
export async function getContractClausesByRun(
  projectUuid: string,
  contractUuid: string,
  runUuid: string,
): Promise<Clause[]> {
  const contract = await apiGetOrNull<{ clauses?: any[] }>(
    `projects/${projectUuid}/data/contracts/${contractUuid}/runs/${runUuid}/clauses`,
  )
  return hydrateClauseComments((contract?.clauses ?? []).map(adaptSemanticClause))
}

export interface ContractWithClauses {
  uuid: string
  projectUuid: string
  folderId: string | null
  nameOfAgreement: string | null
  fileName: string | null
  fullText: string | null
  createdAt: string | null
  updatedAt: string | null
  dateOfAgreement: string | null
  commencementDate: string | null
  contractType: string | null
  category: string | null
  governingCountry: string | null
  governingRegion: string | null
  location: string | null
  currencyCode: string | null
  reportedValue: number | null
  reportedValueCanonical: number | null
  reportedValueCurrency: string | null
  reportedValueLabel: string | null
  reportedValuePeriodEnd: string | null
  reportedValueAccountUuid: string | null
  duration: number | null
  durationUnit: string | null
  riskTier: Contract['riskTier']
  riskBaselineTier: Contract['riskBaselineTier']
  riskReasons: string[] | null
  riskDecidedBy: string | null
  riskJustification: string | null
  riskEvaluatedAt: string | null
  primaryCounterparty: {
    uuid: string
    name: string | null
    address: string | null
    industry: string | null
    otherNames: string[]
  } | null
  parties: {
    uuid: string
    name: string | null
    address: string | null
    industry: string | null
    otherNames: string[]
  }[]
  clauses: (Pick<Clause, 'uuid' | 'ownerContractUuid' | 'ownerContractName' | 'clauseTypeTags' | 'amends' | 'amendedBy' | 'supersedes' | 'supersededBy' | 'supplements' | 'supplementedBy' | 'relatedTo' | 'relatedFrom'> & { heading?: string | null; text?: string | null; comments?: { uuid: string; comment: string; createdAt: string; userId: string | null }[] })[]
  // Contract-level COMPANION edges (document-level supplemental relationship).
  companions: { uuid: string; nameOfAgreement: string | null }[]
  companionOf: { uuid: string; nameOfAgreement: string | null }[]
  comments: {
    uuid: string
    comment: string
    createdAt: string
    userId: string | null
  }[]
  highlights: {
    uuid: string
    highlightText: string
    createdAt: string
    userId: string | null
  }[]
  interactions: {
    uuid: string
    userId: string | null
    createdAt: string
    interactionType: string
  }[]
  reviewedBy: {
    uuid: string
    userId: string
    userName: string
    reviewedAt: string
  }[]
}

// The server clamps a fold page to 500 roots (ProjectDataService#effectiveFoldLimit), and every fold
// read re-folds the WHOLE project (expandClosure) before slicing out the page — so a small page size
// makes the server repeat that full fold once per page. 500 pulls all roots for all but the largest
// projects in a single request (and a single fold), which is what the contract list + count sidebar want.
export const CONTRACT_LIST_PAGE_SIZE = 500

const EMPTY_REF_ARRAY: { uuid: string; heading?: string | null; contract?: { uuid: string; nameOfAgreement?: string | null } | null }[] = []

// Exactly the object tree the vault contract LIST renders, so the server gathers a fold superset and
// trims the rest (clause text, type-assignment justifications, validity issues) per root in one query.
// The list keeps clauseTypeTags + the eight edge refs for its linked-contract badges; reportedValue*
// are re-attached server-side and preserved through the trim. clauseTypeTags/reviewedBy are the
// types/reviews edges projected under their domain keys.
const LIST_EDGE_REF = {
  fields: ['uuid', 'heading'],
  relations: { contract: { fields: ['uuid', 'nameOfAgreement'] } },
}
const LIST_PROJECT = {
  fields: [
    'uuid', 'folderId', 'nameOfAgreement', 'fileName', 'category', 'contractType',
    'governingCountry', 'governingRegion', 'location', 'currencyCode',
    'commencementDate', 'dateOfAgreement', 'duration', 'durationUnit', 'createdAt', 'updatedAt',
    // Backend-persisted Contract Risk with provenance (ADR 0023) for the Risk column's badge. On the
    // folded list read a root's tier is the fold-effective one; a member read alone has its own.
    'riskTier', 'riskBaselineTier', 'riskReasons', 'riskDecidedBy', 'riskJustification', 'riskEvaluatedAt',
  ],
  relations: {
    clauses: {
      fields: ['uuid'],
      // Drop contentless clauses (no tags and no edges): the list renders only tag chips and
      // linked-contract badges, so a bare shell is pure payload.
      dropEmpty: true,
      relations: {
        clauseTypeTags: { relation: 'types', fields: ['uuid', 'clauseType', 'code'] },
        // Lightweight type-assignment details (name/value only) so the list can surface
        // clause-derived fields — Employment Type (Status.employmentType) and the Location
        // clause's city/country. No justification text or validity issues (kept off the list).
        typeAssignments: {
          fields: ['uuid'],
          relations: {
            type: { fields: ['clauseType'] },
            details: { fields: ['name', 'value'] },
          },
        },
        amends: LIST_EDGE_REF, supersedes: LIST_EDGE_REF, supplements: LIST_EDGE_REF,
        amendedBy: LIST_EDGE_REF, supersededBy: LIST_EDGE_REF, supplementedBy: LIST_EDGE_REF,
        relatedTo: LIST_EDGE_REF, relatedFrom: LIST_EDGE_REF,
      },
    },
    primaryCounterparty: { fields: ['uuid', 'name', 'address', 'industry', 'otherNames'] },
    parties: { fields: ['uuid', 'name', 'address', 'industry', 'otherNames'] },
    companions: { fields: ['uuid', 'nameOfAgreement'] },
    companionOf: { fields: ['uuid', 'nameOfAgreement'] },
    comments: { fields: ['uuid', 'comment', 'userId', 'createdAt'] },
    highlights: { fields: ['uuid', 'highlightText', 'userId', 'createdAt'] },
    interactions: { fields: ['uuid', 'interactionType', 'userId', 'createdAt'] },
    reviewedBy: { relation: 'reviews', fields: ['uuid', 'userId', 'userName', 'reviewedAt'] },
  },
}

/** Maps a folded root field-bag (the /data/read fold projection) into ContractWithClauses. */
export function toContractWithClauses(root: Record<string, any>, projectUuid: string): ContractWithClauses {
  const counterparty = root.primaryCounterparty
  return {
    uuid: root.uuid,
    projectUuid,
    folderId: root.folderId ?? null,
    nameOfAgreement: root.nameOfAgreement ?? null,
    fileName: root.fileName ?? null,
    fullText: null, // large document content is not served by the query layer
    createdAt: root.createdAt ?? null,
    updatedAt: root.updatedAt ?? null,
    dateOfAgreement: root.dateOfAgreement ?? null,
    commencementDate: root.commencementDate ?? null,
    contractType: root.contractType ?? null,
    category: root.category ?? null,
    governingCountry: root.governingCountry ?? null,
    governingRegion: root.governingRegion ?? null,
    location: root.location ?? null,
    currencyCode: root.currencyCode ?? null,
    reportedValue: root.reportedValue ?? null,
    reportedValueCanonical: root.reportedValueCanonical ?? null,
    reportedValueCurrency: root.reportedValueCurrency ?? null,
    reportedValueLabel: root.reportedValueLabel ?? null,
    reportedValuePeriodEnd: root.reportedValuePeriodEnd ?? null,
    reportedValueAccountUuid: root.reportedValueAccountUuid ?? null,
    duration: root.duration ?? null,
    durationUnit: root.durationUnit ?? null,
    // Tiers go through asTier so the legacy-colour backstop applies here too (ADR 0026).
    riskTier: asTier(root.riskTier),
    riskBaselineTier: asTier(root.riskBaselineTier),
    riskReasons: root.riskReasons ?? null,
    riskDecidedBy: root.riskDecidedBy ?? null,
    riskJustification: root.riskJustification ?? null,
    riskEvaluatedAt: root.riskEvaluatedAt ?? null,
    primaryCounterparty: counterparty
      ? {
          uuid: counterparty.uuid,
          name: counterparty.name ?? null,
          address: counterparty.address ?? null,
          industry: counterparty.industry ?? null,
          otherNames: counterparty.otherNames ?? [],
        }
      : null,
    parties: (root.parties ?? []).map((p: any) => ({
      uuid: p.uuid,
      name: p.name ?? null,
      address: p.address ?? null,
      industry: p.industry ?? null,
      otherNames: p.otherNames ?? [],
    })),
    clauses: (root.clauses ?? []).map((cl: any) => ({
      uuid: cl.uuid,
      heading: cl.heading ?? null,
      text: cl.text ?? null,
      // The contract this clause belongs to in the folded view (server stamps it on clauses folded
      // in from another contract — a framework/amendment; absent on the root's own clauses). Required
      // by buildRelationshipGraph / deriveLinkedContracts to skip folded-in clauses so their
      // cross-contract edges don't manufacture phantom links (e.g. an MSA framework's clauses folded
      // into a SoW would otherwise link the SoW to its siblings / the framework's own side letters).
      ownerContractUuid: cl.ownerContractUuid ?? undefined,
      ownerContractName: cl.ownerContractName ?? undefined,
      clauseTypeTags: cl.clauseTypeTags ?? [],
      // Type-assignment details → clauseJustifications (carries additionalDetails such as
      // Status.employmentType and Location city/country) so column formatters can read them.
      clauseJustifications: (cl.typeAssignments ?? []).map((ta: any) => ({
        uuid: ta.uuid,
        clauseType: { clauseType: ta.type?.clauseType },
        certainty: ta.certainty ?? 0,
        justification: ta.justification ?? '',
        additionalDetails: (ta.details ?? []).map((d: any) => ({ name: d.name, value: d.value, values: d.values })),
      })),
      comments: cl.comments ?? [],
      amends: cl.amends ?? EMPTY_REF_ARRAY,
      amendedBy: cl.amendedBy ?? EMPTY_REF_ARRAY,
      supersedes: cl.supersedes ?? EMPTY_REF_ARRAY,
      supersededBy: cl.supersededBy ?? EMPTY_REF_ARRAY,
      supplements: cl.supplements ?? EMPTY_REF_ARRAY,
      supplementedBy: cl.supplementedBy ?? EMPTY_REF_ARRAY,
      relatedTo: cl.relatedTo ?? EMPTY_REF_ARRAY,
      relatedFrom: cl.relatedFrom ?? EMPTY_REF_ARRAY,
    })),
    companions: root.companions ?? [],
    companionOf: root.companionOf ?? [],
    comments: root.comments ?? [],
    highlights: root.highlights ?? [],
    interactions: root.interactions ?? [],
    reviewedBy: root.reviewedBy ?? [],
  }
}

/** A document named in a Contract Family (uuid + Name of Agreement). */
export interface FamilyDocumentRef {
  uuid: string
  name: string | null
}

/**
 * One Contract Family of a project as the server derives it (AER-6 REQ-7, ADR 0038): every
 * document of one relationship with one Counterparty, split into its Engagements (fold survivors
 * with what folded into them), with the vendor's standard terms listed as Shared Members and their
 * role in this family. Computed per request, never stored; `key` is for in-page selection only.
 */
export interface ContractFamily {
  key: string
  counterparty: { uuid: string; name: string | null } | null
  /** Every member uuid: Engagement documents and Shared Members. */
  members: string[]
  /** The structural Lead Contract; null when the family has no Engagement (shared terms alone). */
  leadUuid: string | null
  /** Lead first, then latest date first, then name. */
  engagements: (FamilyDocumentRef & { folded: FamilyDocumentRef[] })[]
  sharedMembers: (FamilyDocumentRef & { role: 'IN_FORCE' | 'REPLACED'; replacedBy: FamilyDocumentRef | null })[]
  /** Relationships to documents of other families (AER-6 REQ-16); each raises a Review Flag. */
  crossFamily?: CrossFamilyLink[]
}

export interface FoldedProjectContracts {
  /** One page of folded ROOT contracts (amendments AND supplements consolidated into their originals). */
  contracts: ContractWithClauses[]
  /**
   * The server's fold map: surviving-root uuid → the amendment/superseding children folded into it
   * (the "+N amendments" affordance). {@code /data/read} (fold) computes and returns this; since the server
   * folds before paginating, the client can no longer derive it (a client re-fold over the returned
   * roots sees no children), so we read it straight off the response.
   */
  childrenByRoot: Record<string, FoldedChild[]>
  /**
   * The server's supplement fold map: surviving-root uuid → the contracts that supplement it and were
   * folded into it (the "+N supplements" affordance). Only populated when the read requests
   * {@code foldSupplements}; same "can't re-derive client-side" reasoning as {@code childrenByRoot}.
   */
  supplementsByRoot: Record<string, FoldedChild[]>
  /**
   * The server's related-documents map: surviving-root uuid → every document folded under it, tagged
   * by {@code kind}. Only the {@code "superseded"} (contract-level SUPERSEDES) and {@code "cancelled"}
   * (contract-level CANCELS) entries are of interest here — those predecessors are folded OUT of the
   * root list, so unlike clause/companion edges they can only be surfaced from this map. Same
   * "can't re-derive client-side" reasoning as {@code childrenByRoot}; mirrors {@code foldedContracts}.
   */
  relatedByRoot: Record<string, RelatedContractDto[]>
  /**
   * The project's Contract Families (AER-6 REQ-7): the whole project's families, not just this
   * page's — the vault groups its rows by these and derives no grouping of its own.
   */
  families: ContractFamily[]
  /** Server fold-pagination flag: true when more surviving roots remain past this page. */
  hasMore: boolean
  /** The offset to request next when {@code hasMore}, else null. */
  nextOffset: number | null
}

/**
 * The vault contract list, one page of folded ROOT contracts (amendments AND supplements consolidated
 * into their originals). Server-side root pagination via /data/read with fold:true: offset/pageSize page
 * over surviving roots, never raw rows. The LIST_PROJECT tree gathers the full per-contract relationship
 * graph the list's linked-contract badges + review state need. Returns the page's roots plus the server's
 * whole {@code childrenByRoot} / {@code supplementsByRoot} fold maps (for the +N amendments / +N supplements badges).
 */
export async function getContractsWithClausesByProject(
  projectUuid: string,
  offset: number,
  pageSize: number = CONTRACT_LIST_PAGE_SIZE,
): Promise<FoldedProjectContracts> {
  const res = await apiPost<{
    objects?: Record<string, any>[]
    childrenByRoot?: Record<string, FoldedChild[]>
    supplementsByRoot?: Record<string, FoldedChild[]>
    relatedByRoot?: Record<string, RelatedContractDto[]>
    families?: ContractFamily[]
    hasMore?: boolean
    nextOffset?: number | null
  }>(
    `projects/${projectUuid}/data/read`,
    {
      entity: 'contract',
      alias: 'c',
      filters: [],
      offset,
      limit: pageSize,
      fold: true,
      expandClosure: true,
      // The Contract Families of the whole project (AER-6): the vault lists one row per family.
      families: true,
      // Fold supplements (companion documents that add to, but don't replace, an original) into the
      // contract they supplement, exactly as the intelligence dashboards do — the folded-away supplements
      // stay reachable via the "+N supplements" name badge and the Relationships column.
      foldSupplements: true,
      // Per-request projection: the fold gathers a superset and trims each root to exactly what the
      // contract list renders (no clause text, type-assignment justifications or validity issues).
      project: LIST_PROJECT,
    },
  )
  return {
    contracts: (res.objects ?? []).map((root) => toContractWithClauses(root, projectUuid)),
    childrenByRoot: res.childrenByRoot ?? {},
    supplementsByRoot: res.supplementsByRoot ?? {},
    relatedByRoot: res.relatedByRoot ?? {},
    families: res.families ?? [],
    hasMore: res.hasMore ?? false,
    nextOffset: res.nextOffset ?? null,
  }
}

/**
 * Fetches specific contracts by uuid in the SAME row shape as the vault list ({@link LIST_PROJECT}
 * projection via {@link toContractWithClauses}). Used to hydrate the documents folded into a root
 * (amendments, supplements, superseded/cancelled/framework docs) when a row is expanded — those
 * members are folded OUT of the list and arrive there only as {uuid,name,date} refs.
 *
 * fold:false is essential: these ARE folded children, so folding would consolidate them away again
 * and return nothing. The uuids are pinned with the semantic layer's `in` operator (values list).
 */
export async function getContractsByUuids(
  projectUuid: string,
  uuids: string[],
): Promise<ContractWithClauses[]> {
  if (uuids.length === 0) return []
  const res = await apiPost<{ objects?: Record<string, any>[] }>(
    `projects/${projectUuid}/data/read`,
    {
      entity: 'contract',
      alias: 'c',
      filters: [{ property: 'uuid', op: 'in', values: uuids }],
      offset: 0,
      limit: uuids.length,
      fold: false,
      expandClosure: true,
      project: LIST_PROJECT,
    },
  )
  return (res.objects ?? []).map((root) => toContractWithClauses(root, projectUuid))
}

// Contract relationship derivation (clause-level + document-level COMPANION) lives in the pure,
// testable module lib/relationshipGraph. Re-exported here for existing import sites.
export { deriveLinkedContracts, buildRelationshipGraph, kindEdgeType } from '@/lib/relationshipGraph'

export interface CoordinatePoint {
  pageNum: number
  topLeft: { x: number; y: number }
  topRight: { x: number; y: number }
  bottomLeft: { x: number; y: number }
  bottomRight: { x: number; y: number }
}

export interface CreateUserClauseRequest {
  contractUuid: string
  text: string
  /** Canonical AABB-in-PDF-points clause boxes, converted from the two-click selection at
   *  commit time. Distinct from the highlight corner shape (CoordinatePoint) — see ADR 0005. */
  boundingBoxes?: BoundingBox[]
  clauseTypeTags?: { clauseType: string }[]
  clauseJustifications?: {
    clauseType: { clauseType: string }
    certainty: number
    justification: string
  }[]
  createdByUserId?: string
  /** Optional client-generated idempotency key — the server MERGEs the clause on it (ADR 0010). */
  clientId?: string
}

export interface UpdateClauseTagsRequest {
  clauseUuid: string
  addedTags: { clauseType: string }[]
  justifications: {
    clauseType: { clauseType: string }
    certainty: number
    justification: string
  }[]
}

function toBoundingBoxInput(coord: CoordinatePoint) {
  return {
    pageNum: coord.pageNum,
    topLeftX: coord.topLeft.x,
    topLeftY: coord.topLeft.y,
    topRightX: coord.topRight.x,
    topRightY: coord.topRight.y,
    bottomLeftX: coord.bottomLeft.x,
    bottomLeftY: coord.bottomLeft.y,
    bottomRightX: coord.bottomRight.x,
    bottomRightY: coord.bottomRight.y,
  }
}

export async function createUserClause(
  projectUuid: string,
  request: CreateUserClauseRequest,
): Promise<Clause | null> {
  const result = await apiPost<{ uuid: string }>(
    `projects/${projectUuid}/contracts/${request.contractUuid}/clauses`,
    {
      text: request.text,
      clientId: request.clientId ?? null,
      createdByUserId: request.createdByUserId ?? null,
      clauseTypeTags: request.clauseTypeTags?.map((t) => ({ clauseType: t.clauseType })) ?? [],
      clauseJustifications:
        request.clauseJustifications?.map((j) => ({
          clauseType: j.clauseType.clauseType,
          certainty: j.certainty,
          justification: j.justification,
        })) ?? [],
      boundingBoxes: request.boundingBoxes ?? [],
    },
  )
  // Reconstruct a minimal optimistic clause from the request + the server-assigned uuid;
  // a refetch fills in the rest (tags, justifications, bounding boxes).
  return result?.uuid
    ? ({
        uuid: result.uuid,
        text: request.text,
        clauseTypeTags: [],
        clauseJustifications: [],
        isUserCreated: true,
      } as Clause)
    : null
}

export async function updateClauseTags(
  projectUuid: string,
  updates: UpdateClauseTagsRequest[],
): Promise<void> {
  if (updates.length === 0) return
  // One batched request (was one mutation per clause).
  await apiPost(
    `projects/${projectUuid}/clauses/tags`,
    updates.map((update) => ({
      clauseUuid: update.clauseUuid,
      addedTags: update.addedTags.map((t) => ({ clauseType: t.clauseType })),
      justifications: update.justifications.map((j) => ({
        clauseType: j.clauseType.clauseType,
        certainty: j.certainty,
        justification: j.justification,
      })),
    })),
  )
}

export async function updateClauseReviewStatus(
  projectUuid: string,
  clauseUuid: string,
  reviewStatus: 'approved' | 'rejected',
  reviewedByUserId?: string,
): Promise<void> {
  await apiPut(`projects/${projectUuid}/clauses/${clauseUuid}/review-status`, {
    reviewStatus: reviewStatus.toUpperCase(), // server enum is APPROVED | REJECTED
    reviewedByUserId: reviewedByUserId ?? null,
  })
}

export async function appendClauseComments(
  projectUuid: string,
  clauseUuid: string,
  comments: PersistedCommentInput[],
): Promise<void> {
  if (comments.length === 0) return
  await apiPost(
    `projects/${projectUuid}/clauses/${clauseUuid}/comments`,
    comments.map(toCommentInputVariable),
  )
}

export async function appendContractComments(
  projectUuid: string,
  contractUuid: string,
  comments: PersistedCommentInput[],
): Promise<void> {
  if (comments.length === 0) return
  await apiPost(
    `projects/${projectUuid}/contracts/${contractUuid}/comments`,
    comments.map(toCommentInputVariable),
  )
}

/** One extracted-detail value edit: the detail's camelCase key, its (joined) display value, and the
 *  structured list for a multi-valued detail (omit/[] for single-valued). */
export interface DetailValueInput {
  name: string
  value: string
  values?: string[]
}

/**
 * Upsert the extracted-detail VALUES on a clause-type justification (WRITE). The server MERGEs each
 * detail by name under the justification and sets value + values together — editing the detail in
 * place when it exists, creating it when extraction produced none. MERGE-by-name means a detail can
 * never be added twice. A 404 means the justification was not found in the project.
 */
export async function updateClauseDetailValues(
  projectUuid: string,
  justificationUuid: string,
  details: DetailValueInput[],
): Promise<void> {
  if (details.length === 0) return
  await apiPut(
    `projects/${projectUuid}/clause-type-justifications/${justificationUuid}/details`,
    details.map((d) => ({ name: d.name, value: d.value, values: d.values ?? [] })),
  )
}

export async function appendClauseTypeJustificationComments(
  projectUuid: string,
  justificationUuid: string,
  comments: PersistedCommentInput[],
): Promise<void> {
  if (comments.length === 0) return
  await apiPost(
    `projects/${projectUuid}/clause-type-justifications/${justificationUuid}/comments`,
    comments.map(toCommentInputVariable),
  )
}

export async function appendHighlightComments(
  projectUuid: string,
  highlightUuid: string,
  comments: PersistedCommentInput[],
): Promise<void> {
  if (comments.length === 0) return
  await apiPost(
    `projects/${projectUuid}/highlights/${highlightUuid}/comments`,
    comments.map(toCommentInputVariable),
  )
}

export async function removeClauseTags(
  projectUuid: string,
  clauseUuid: string,
  clauseTypes: string[],
): Promise<void> {
  if (clauseTypes.length === 0) return
  await apiDelete(`projects/${projectUuid}/clauses/${clauseUuid}/tags`, { clauseTypes })
}

/** The reviewed-correct (HAS_TYPE) and flagged-incorrect (FLAGGED_INCORRECT_TYPE) tag
 * sets of a clause, as returned by the flag/un-flag mutations for state reconciliation. */
export interface ClauseTagSets {
  clauseTypeTags: { clauseType: string }[]
  incorrectClauseTypeTags: { clauseType: string }[]
}


function toTagSets(node: any): ClauseTagSets {
  return {
    clauseTypeTags: node?.clauseTypeTags ?? [],
    incorrectClauseTypeTags: node?.incorrectClauseTypeTags ?? [],
  }
}

/**
 * Flag one or more AI clause-type tags on a clause as incorrect (reviewer feedback).
 * Moves the tag off HAS_TYPE to FLAGGED_INCORRECT_TYPE server-side (and its matching
 * justification off JUSTIFIES_TYPE), so it stops counting in every dashboard.
 * Returns the clause's updated correct + flagged tag sets.
 */
export async function flagClauseTagsIncorrect(
  projectUuid: string,
  clauseUuid: string,
  clauseTypes: string[],
): Promise<ClauseTagSets> {
  if (clauseTypes.length === 0) return { clauseTypeTags: [], incorrectClauseTypeTags: [] }
  const result = await apiPost<ClauseTagSets, { clauseTypes: string[] }>(
    `projects/${projectUuid}/clauses/${clauseUuid}/tags/flag-incorrect`,
    { clauseTypes },
  )
  return toTagSets(result)
}

/** Clear the incorrect flag from one or more clause-type tags on a clause (un-flag):
 * moves them back to HAS_TYPE / JUSTIFIES_TYPE. Returns the updated tag sets. */
export async function unflagClauseTags(
  projectUuid: string,
  clauseUuid: string,
  clauseTypes: string[],
): Promise<ClauseTagSets> {
  if (clauseTypes.length === 0) return { clauseTypeTags: [], incorrectClauseTypeTags: [] }
  const result = await apiPost<ClauseTagSets, { clauseTypes: string[] }>(
    `projects/${projectUuid}/clauses/${clauseUuid}/tags/unflag-incorrect`,
    { clauseTypes },
  )
  return toTagSets(result)
}

/**
 * Re-runs the backend risk evaluation for one contract's whole fold family (`RiskController`) —
 * one of the three explicit evaluation triggers of ADR 0023, and the only one a reader-facing
 * surface can pull. EDITOR-gated server-side, because evaluating WRITES persisted tiers with
 * provenance. Synchronous and potentially slow: when the customer has a published risk policy it
 * includes an LLM call per contract, so callers must keep the affordance disabled until it returns
 * and refetch the contract + clauses afterwards — the tiers it wrote are the point.
 */
export async function evaluateContractRisk(
  projectUuid: string,
  contractUuid: string,
): Promise<void> {
  await apiPost(`projects/${projectUuid}/risk/contracts/${contractUuid}/evaluate`, {})
}

export async function markContractReviewed(
  projectUuid: string,
  contractUuid: string,
  userId: string,
  userName: string,
): Promise<void> {
  await apiPost(`projects/${projectUuid}/contracts/${contractUuid}/reviews`, { userId, userName })
}

export async function unmarkContractReviewed(
  projectUuid: string,
  contractUuid: string,
  userId: string,
): Promise<void> {
  await apiDelete(`projects/${projectUuid}/contracts/${contractUuid}/reviews/${userId}`)
}

export async function appendContractHighlights(
  projectUuid: string,
  contractUuid: string,
  highlights: PersistedHighlightInput[],
): Promise<void> {
  if (highlights.length === 0) return
  await apiPost(
    `projects/${projectUuid}/contracts/${contractUuid}/highlights`,
    highlights.map((h) => ({
      highlightText: h.highlightText,
      createdAt: h.createdAt,
      userId: h.userId ?? null,
      comment: h.comment?.trim() ? h.comment.trim() : null,
      boundingBoxes: h.coordinates?.map(toBoundingBoxInput) ?? [],
      clientId: h.clientId ?? null,
    })),
  )
}

/** Fetch a contract's saved highlights (with normalized bounding boxes + comments). */
export async function getContractHighlights(
  projectUuid: string,
  contractUuid: string,
): Promise<SavedPdfHighlight[]> {
  // The API returns `highlightText`; the view model uses `text` (matching pending highlights).
  const raw =
    (await apiGetOrNull<Array<Omit<SavedPdfHighlight, 'text'> & { highlightText: string }>>(
      `projects/${projectUuid}/contracts/${contractUuid}/highlights`,
    )) ?? []
  return raw.map(({ highlightText, ...rest }) => ({ ...rest, text: highlightText }))
}

/** Permanently delete a single saved highlight (and its bounding boxes + comments). */
export async function deleteContractHighlight(
  projectUuid: string,
  contractUuid: string,
  highlightUuid: string,
): Promise<void> {
  await apiDelete(`projects/${projectUuid}/contracts/${contractUuid}/highlights/${highlightUuid}`)
}

// ─── Reprocess (re-extraction) ─────────────────────────────────────────────────
// Re-runs the LLM extraction on existing contracts, superseding their old AI clauses
// and writing fresh ones under a new ExtractionRun. Version ids are optional: when
// omitted, each contract's customer's currently-published prompt/taxonomy are pinned
// at enqueue. Gated to project EDITOR+ server-side. Progress is polled via the batch
// status endpoint (the SSE stream is the primary channel; polling is the fallback).

export type ReprocessStatus = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'SUPERSEDED';

export interface ReprocessBatchItem {
  contractUuid: string
  runNumber: number | null
  status: ReprocessStatus
  detail: string | null
}

export interface ReprocessBatchStatus {
  batchUuid: string
  total: number
  completed: number
  failed: number
  running: number
  done: boolean
  contracts: ReprocessBatchItem[]
}

export interface ReprocessOptions {
  /** Pin the process_contract prompt to this version (omit = current published). */
  promptVersionId?: string
  /** Pin the taxonomy to this version (omit = current published). */
  taxonomyVersionId?: string
}

/** Enqueue a reprocess batch; resolves to the batch id to poll. */
export async function reprocessContracts(
  projectUuid: string,
  contractUuids: string[],
  opts?: ReprocessOptions,
): Promise<{ batchUuid: string }> {
  return apiPost<{ batchUuid: string }, {
    contractUuids: string[]
    promptVersionId?: string
    taxonomyVersionId?: string
  }>(`projects/${projectUuid}/contracts/reprocess`, {
    contractUuids,
    promptVersionId: opts?.promptVersionId,
    taxonomyVersionId: opts?.taxonomyVersionId,
  })
}

/** Poll the status of a reprocess batch. */
export async function getReprocessBatch(
  projectUuid: string,
  batchUuid: string,
): Promise<ReprocessBatchStatus> {
  return apiFetch<ReprocessBatchStatus>(
    `projects/${projectUuid}/contracts/reprocess/${batchUuid}`,
  )
}
